/**
 * use-pristine — track whether a form's current values match the last
 * saved/hydrated baseline.
 *
 * Per F-007 follow-up (2026-05-22): the visual indicator that a form is
 * "saved" is the save button being disabled. No timestamp banners — the
 * button state IS the confirmation. When the user changes anything,
 * the button re-enables.
 *
 * Usage:
 *
 *     const [form, setForm] = useState(DEFAULT_FORM);
 *     const { isPristine, markSaved } = usePristine(DEFAULT_FORM);
 *
 *     useEffect(() => {
 *       if (existing) {
 *         setForm(existing);
 *         markSaved(existing);  // baseline = hydrated values
 *       }
 *     }, [existing]);
 *
 *     async function onSubmit() {
 *       await api.save(form);
 *       markSaved(form);  // baseline = just-saved values
 *     }
 *
 *     <button disabled={isPristine(form) || submitting}>Save</button>
 *
 * The hook serialises values with JSON.stringify. That assumes plain
 * data — primitives, arrays, objects of the same. Don't put Date,
 * Map, Set, or functions in the tracked shape; coerce to ISO strings
 * before tracking.
 */

import { useCallback, useState } from 'react';

export interface UsePristineResult<T> {
  /** True iff `current` matches the last saved/hydrated baseline. */
  isPristine: (current: T) => boolean;
  /**
   * Reset the baseline to the given values. Call after a successful save
   * or after hydration completes. Passing the same shape you tracked
   * with `isPristine` keeps the comparison consistent.
   */
  markSaved: (current: T) => void;
}

export function usePristine<T>(initial: T): UsePristineResult<T> {
  const [baseline, setBaseline] = useState<string>(() => JSON.stringify(initial));
  const markSaved = useCallback((current: T): void => {
    setBaseline(JSON.stringify(current));
  }, []);
  return {
    isPristine: (current: T): boolean => JSON.stringify(current) === baseline,
    markSaved,
  };
}
